import {
  Cell as AriaCell,
  Column as AriaColumn,
  Row as AriaRow,
  Table as AriaTable,
  TableBody as AriaTableBody,
  TableHeader as AriaTableHeader,
  type CellProps as AriaCellProps,
  type ColumnProps as AriaColumnProps,
  type RowProps as AriaRowProps,
  type TableBodyProps as AriaTableBodyProps,
  type TableHeaderProps as AriaTableHeaderProps,
  type TableProps as AriaTableProps,
} from 'react-aria-components';

import styles from './Table.module.css';

type Unstyled<P> = Omit<P, 'className' | 'style'>;

export interface TableProps extends Unstyled<AriaTableProps> {
  /** 36px rows instead of 44px. */
  dense?: boolean;
  /**
   * No frame of its own, for a table that is the whole content of a card (a `Section` or
   * an untitled panel): the card is the frame, so there is no double border.
   */
  flat?: boolean;
}

/**
 * A data table (DESIGN.md §7) in a white, hairline-framed container. On narrow screens a
 * wide table scrolls sideways inside its own container, so the page itself never does.
 */
export function Table({ dense = false, flat = false, ...rest }: TableProps) {
  return (
    <div className={styles.scroller} data-flat={flat || undefined}>
      <AriaTable {...rest} className={styles.table} data-dense={dense || undefined} />
    </div>
  );
}

export function TableHeader<T extends object>(props: Unstyled<AriaTableHeaderProps<T>>) {
  return <AriaTableHeader {...props} />;
}

export interface ColumnProps extends Unstyled<AriaColumnProps> {
  /** Right-aligned with tabular figures: amounts, quantities, lab values, waiting times. */
  numeric?: boolean;
}

export function Column({ numeric = false, ...rest }: ColumnProps) {
  return <AriaColumn {...rest} className={styles.column} data-numeric={numeric || undefined} />;
}

export function TableBody<T extends object>(props: Unstyled<AriaTableBodyProps<T>>) {
  return <AriaTableBody {...props} className={styles.body} />;
}

export function Row<T extends object>(props: Unstyled<AriaRowProps<T>>) {
  return <AriaRow {...props} className={styles.row} />;
}

export interface CellProps extends Unstyled<AriaCellProps> {
  numeric?: boolean;
}

export function Cell({ numeric = false, ...rest }: CellProps) {
  return <AriaCell {...rest} className={styles.cell} data-numeric={numeric || undefined} />;
}
